import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

type UseSessionStorageStateOptions<T> = {
  key: string;
  initialValue?: T;
};

const readValue = <T>(key: string, initialValue?: T): T => {
  try {
    const stored = window.sessionStorage.getItem(key);
    return stored === null ? (initialValue as T) : (JSON.parse(stored) as T);
  } catch {
    return initialValue as T;
  }
};

const writeValue = (key: string, value: unknown): void => {
  try {
    if (value == null) {
      window.sessionStorage.removeItem(key);
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // sessionStorage can throw in private-mode/disabled-storage browsers; persistence is best-effort.
  }
};

export const useSessionStorageState = <T>({
  key,
  initialValue,
}: UseSessionStorageStateOptions<T>): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => readValue(key, initialValue));

  const setStoredValue: Dispatch<SetStateAction<T>> = useCallback(
    (next) => {
      setValue((prev) => {
        // oxlint-disable-next-line unicorn/no-instanceof-builtins
        const resolved = next instanceof Function ? next(prev) : next;
        writeValue(key, resolved);
        return resolved;
      });
    },
    [key],
  );

  return [value, setStoredValue];
};
